import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';

const DisneylandResortPackageForm = ({ onSave, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    title: 'Disneyland Resort Package',
    resort_name: '',
    room_type: '',
    view: '',
    check_in_date: '',
    check_out_date: '',
    ticket_type: '2-Day Ticket',
    ticket_options: 'Base Ticket',
    ticket_validity_start: '',
    ticket_validity_end: '',
    ground_transportation: '',
    travel_protection: 'Not Included',
    travel_protection_price: 0,
    total_price: 0,
    total_price_due_by: '',
    deposit_due: 0,
    deposit_due_by: '',
    special_requests: '',
    children: [],
    isOption: false,
    optionDescription: '',
    photos: [],
    documents: [],
    ...initialData
  });

  const [errors, setErrors] = useState({});

  // Disneyland Resort Properties (3 official hotels)
  const resortOptions = [
    // Disneyland Resort Hotels
    "Disney's Grand Californian Hotel & Spa",
    "Disney's Pixar Place Hotel", 
    "Disneyland Hotel",
    // Good Neighbor Hotels (2026 program list)
    "Anaheim Majestic Garden Hotel",
    "Anaheim Marriott",
    "Anaheim Marriott Suites",
    "Anaheim Portofino Inn & Suites",
    "Anaheim Resort Suites",
    "Best Western Plus Anaheim Inn",
    "Best Western Plus Park Place Inn & Mini Suites",
    "Best Western Plus Pavilions",
    "Best Western Plus Raffles Inn & Suites",
    "Best Western Plus Stovall's Inn",
    "Camelot Inn & Suites",
    "Candlewood Suites Anaheim Resort Area",
    "Candy Cane Inn",
    "Castle Inn & Suites",
    "Clarion Hotel Anaheim Resort",
    "Clementine Hotel & Suites Anaheim",
    "Cortona Inn & Suites Anaheim Resort",
    "Courtyard by Marriott Anaheim Resort Convention Center",
    "Courtyard by Marriott Anaheim Theme Park Entrance",
    "Delta Hotels by Marriott Anaheim Garden Grove",
    "Desert Palms Hotel & Suites Anaheim Resort",
    "DoubleTree Suites by Hilton Hotel Anaheim Resort Convention Center",
    "Element by Westin Anaheim Resort Convention Center",
    "Embassy Suites by Hilton Anaheim South",
    "Fairfield Inn by Marriott Anaheim Resort",
    "Four Points by Sheraton Anaheim",
    "Grand Legacy at the Park",
    "Hampton Inn & Suites by Hilton Anaheim Garden Grove",
    "Hampton Inn & Suites by Hilton Anaheim Resort Convention Center",
    "Hilton Anaheim",
    "Hilton Garden Inn Anaheim Garden Grove",
    "Hilton Garden Inn Anaheim Resort",
    "Holiday Inn & Suites Anaheim",
    "Holiday Inn Express & Suites Anaheim Resort Area",
    "Home2 Suites by Hilton Anaheim Resort",
    "Homewood Suites by Hilton Anaheim Maingate Area",
    "Homewood Suites by Hilton Anaheim Resort Convention Center",
    "Hotel Lulu Anaheim Resort",
    "Howard Johnson Anaheim Hotel and Water Playground",
    "Hyatt House at Anaheim Resort Convention Center",
    "Hyatt Place at Anaheim Resort Convention Center",
    "Hyatt Regency Orange County",
    "JW Marriott Anaheim Resort",
    "Motel 6 Anaheim Maingate",
    "Residence Inn Anaheim Resort Convention Center",
    "Residence Inn by Marriott Anaheim Resort Area Garden Grove",
    "Sheraton Garden Grove - Anaheim South",
    "Sheraton Park Hotel at the Anaheim Resort",
    "Sonesta Anaheim Resort Area",
    "Sonesta ES Suites Anaheim Resort Area",
    "SpringHill Suites Anaheim Resort Convention Center",
    "SpringHill Suites by Marriott Anaheim Maingate",
    "SunCoast Park Hotel Anaheim, Tapestry Collection by Hilton",
    "The Anaheim Hotel",
    "The Viv Hotel, Anaheim, a Tribute Portfolio Hotel",
    "The Westin Anaheim Resort",
    "Tropicana Inn & Suites"
  ];

  const ticketTypes = [
    '1-Day Ticket', '2-Day Ticket', '3-Day Ticket', '4-Day Ticket', '5-Day Ticket'
  ];

  const ticketOptionsChoices = [
    'Base Ticket',
    'Base Ticket + Lightning Lane',
    'Park Hopper Ticket',
    'Park Hopper Ticket + Lightning Lane'
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleChildrenChange = (count) => {
    const children = Array.from({ length: count }, (_, i) => 
      formData.children[i] || { age: 5 }
    );
    setFormData(prev => ({ ...prev, children }));
  };

  const handleChildAgeChange = (index, age) => {
    const updatedChildren = [...formData.children];
    updatedChildren[index] = { age: parseInt(age) };
    setFormData(prev => ({ ...prev, children: updatedChildren }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.check_in_date) newErrors.check_in_date = 'Check-in date is required';
    if (!formData.check_out_date) newErrors.check_out_date = 'Check-out date is required';
    if (!formData.resort_name) newErrors.resort_name = 'Resort selection is required';
    if (!formData.total_price) newErrors.total_price = 'Total price is required';
    if (!formData.deposit_due) newErrors.deposit_due = 'Deposit amount is required';

    // Date validation
    if (formData.check_in_date && formData.check_out_date) {
      const checkIn = new Date(formData.check_in_date);
      const checkOut = new Date(formData.check_out_date);
      if (checkOut <= checkIn) {
        newErrors.check_out_date = 'Check-out date must be after check-in date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Disneyland form submitted with data:', formData);
    console.log('Form validation result:', validateForm());
    
    if (validateForm()) {
      const componentData = {
        type: 'disneyland-resort-package',
        title: formData.title,
        price: formData.total_price,
        isOption: formData.isOption,
        optionDescription: formData.optionDescription,
        paymentTiming: formData.paymentTiming,
        optionalDepositAmount: formData.optionalDepositAmount,
        photos: formData.photos,
        documents: formData.documents,
        data: {
          resort_name: formData.resort_name,
          room_type: formData.room_type,
          view: formData.view,
          check_in_date: formData.check_in_date,
          check_out_date: formData.check_out_date,
          ticket_type: formData.ticket_type,
          ticket_options: formData.ticket_options,
          ticket_validity_start: formData.ticket_validity_start,
          ticket_validity_end: formData.ticket_validity_end,
          ground_transportation: formData.ground_transportation,
          travel_protection: formData.travel_protection,
          travel_protection_price: formData.travel_protection_price,
          total_price: formData.total_price,
          total_price_due_by: formData.total_price_due_by,
          deposit_due: formData.deposit_due,
          deposit_due_by: formData.deposit_due_by,
          special_requests: formData.special_requests,
          children: formData.children
        }
      };
      console.log('Calling onSave with:', componentData);
      onSave(componentData);
    } else {
      console.log('Form validation failed with errors:', errors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="component-form disneyland-package-form">
      <div className="form-section">
        <h4>Package Details</h4>
        <div className="form-group">
          <label>Package Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Disneyland Resort Package"
            className="form-control"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Resort Accommodation</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Check-in Date *</label>
            <input
              type="date"
              name="check_in_date"
              value={formData.check_in_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_in_date ? 'error' : ''}`}
            />
            {errors.check_in_date && <span className="error-text">{errors.check_in_date}</span>}
          </div>
          <div className="form-group">
            <label>Check-out Date *</label>
            <input
              type="date"
              name="check_out_date"
              value={formData.check_out_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_out_date ? 'error' : ''}`}
            />
            {errors.check_out_date && <span className="error-text">{errors.check_out_date}</span>}
          </div>
        </div>
        
        <div className="form-group">
          <label>Resort/Hotel Name *</label>
          <select
            name="resort_name"
            value={formData.resort_name}
            onChange={handleInputChange}
            className={`form-control ${errors.resort_name ? 'error' : ''}`}
          >
            <option value="">Select a Resort/Hotel</option>
            <optgroup label="Disneyland Resort Hotels">
              <option value="Disney's Grand Californian Hotel & Spa">Disney's Grand Californian Hotel & Spa</option>
              <option value="Disney's Pixar Place Hotel">Disney's Pixar Place Hotel</option>
              <option value="Disneyland Hotel">Disneyland Hotel</option>
            </optgroup>
            <optgroup label="Good Neighbor Hotels">
              {resortOptions.slice(3).map(resort => (
                <option key={resort} value={resort}>{resort}</option>
              ))}
            </optgroup>
          </select>
          {errors.resort_name && <span className="error-text">{errors.resort_name}</span>}
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Room Type</label>
            <input
              type="text"
              name="room_type"
              value={formData.room_type}
              onChange={handleInputChange}
              placeholder="e.g., Standard Room, Suite"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>View</label>
            <input
              type="text"
              name="view"
              value={formData.view}
              onChange={handleInputChange}
              placeholder="e.g., Park View, Pool View"
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Theme Park Tickets</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Ticket Type</label>
            <select
              name="ticket_type"
              value={formData.ticket_type}
              onChange={handleInputChange}
              className="form-control"
            >
              {ticketTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Ticket Options</label>
            <select
              name="ticket_options"
              value={formData.ticket_options}
              onChange={handleInputChange}
              className="form-control"
            >
              {ticketOptionsChoices.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Ticket Validity Start</label>
            <input
              type="date"
              name="ticket_validity_start"
              value={formData.ticket_validity_start}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Ticket Validity End</label>
            <input
              type="date"
              name="ticket_validity_end"
              value={formData.ticket_validity_end}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Add-on Services</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Ground Transportation</label>
            <input
              type="text"
              name="ground_transportation"
              value={formData.ground_transportation}
              onChange={handleInputChange}
              placeholder="Transportation details"
              className="form-control"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Travel Protection</label>
            <select
              name="travel_protection"
              value={formData.travel_protection}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="Not Included">Not Included</option>
              <option value="Included">Included</option>
            </select>
          </div>
          {formData.travel_protection === 'Included' && (
            <div className="form-group">
              <label>Travel Protection Price ($)</label>
              <input
                type="number"
                name="travel_protection_price"
                value={formData.travel_protection_price}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className="form-control"
              />
            </div>
          )}
        </div>
      </div>

      <div className="form-section">
        <h4>Pricing & Payment</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Total Price ($) *</label>
            <input
              type="number"
              name="total_price"
              value={formData.total_price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.total_price ? 'error' : ''}`}
            />
            {errors.total_price && <span className="error-text">{errors.total_price}</span>}
          </div>
          <div className="form-group">
            <label>Total Price Due By</label>
            <input
              type="date"
              name="total_price_due_by"
              value={formData.total_price_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Deposit Due ($) *</label>
            <input
              type="number"
              name="deposit_due"
              value={formData.deposit_due}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.deposit_due ? 'error' : ''}`}
            />
            {errors.deposit_due && <span className="error-text">{errors.deposit_due}</span>}
          </div>
          <div className="form-group">
            <label>Deposit Due By</label>
            <input
              type="date"
              name="deposit_due_by"
              value={formData.deposit_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Additional Information</h4>
        <div className="form-group">
          <label>Special Requests</label>
          <textarea
            name="special_requests"
            value={formData.special_requests}
            onChange={handleInputChange}
            rows="3"
            className="form-control"
            placeholder="Any special requests or preferences for this Disneyland package"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Client Options</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOption}
                onChange={(e) => setFormData({...formData, isOption: e.target.checked})}
              />
              Mark as Optional for Client
            </label>
            <small className="form-help">
              When checked, clients can choose whether to include this component in their booking
            </small>
          </div>
        </div>

        {formData.isOption && (
          <>
            <div className="form-group">
              <label>Option Description for Client</label>
              <textarea
                value={formData.optionDescription}
                onChange={(e) => setFormData({...formData, optionDescription: e.target.value})}
                placeholder="e.g., Optional Disneyland package upgrade or add-on"
                rows="2"
              />
              <small className="form-help">
                This description helps clients understand what this option includes
              </small>
            </div>

            <div className="form-group">
              <label>Payment Timing for Optional Component</label>
              <select
                value={formData.paymentTiming}
                onChange={(e) => setFormData({...formData, paymentTiming: e.target.value})}
                className="form-control"
              >
                <option value="immediate">Pay Full Price When Selected (e.g., cruise excursions)</option>
                <option value="with_deposit">Pay Deposit When Selected</option>
                <option value="with_final">Pay with Final Payment (nothing due initially)</option>
                <option value="bundled">Already Included in Package (no additional payment)</option>
              </select>
              <small className="form-help">
                Choose when payment is due if client selects this option
              </small>
            </div>

            {formData.paymentTiming === 'with_deposit' && (
              <div className="form-group">
                <label>Optional Component Deposit Amount ($)</label>
                <input
                  type="number"
                  value={formData.optionalDepositAmount}
                  onChange={(e) => setFormData({...formData, optionalDepositAmount: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="form-control"
                />
                <small className="form-help">
                  Amount due when client selects this option (leave blank for full price)
                </small>
              </div>
            )}
          </>
        )}
      </div>

      <AttachmentUpload
        photos={formData.photos || []}
        documents={formData.documents || []}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Add to Quote
        </button>
      </div>
    </form>
  );
};

export default DisneylandResortPackageForm;
